import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Image } from "react-bootstrap";
import {
  GeoAlt,
  Pen,
  People,
  ShieldShaded,
  Coin,
  Tree,
  Globe2,
  ReceiptCutoff,
  CaretLeftFill,
  Building,
  Palette,
  Sun,
  Moon,
  CheckCircleFill,
  BrightnessHighFill,
  CircleHalf
} from "react-bootstrap-icons";
import { getProvincialInfo } from "../../utils/canadaFeedsConfig";
import { getMexicanStateInfo } from "../../utils/mexicoFeedsConfig";
import {
  MODES,
  DEFAULT_LIGHT_THEME,
  DEFAULT_DARK_THEME,
  getAutoSide,
  resolveTheme,
  applyTheme,
  applyStoredTheme,
  setAutoEnabled,
  selectTheme
} from '../../utils/themeUtils';
import { FCM } from "@capacitor-community/fcm";
import { PushNotifications } from "@capacitor/push-notifications";
import { Capacitor } from "@capacitor/core";
import { getUKRegionInfo } from "../../utils/unitedKingdomFeedsConfig";
import migrateOnboardingData, { getStoredTopics } from "./onboardingMigration";
import { useTranslation } from 'react-i18next';
import { notifyOnboardingFlagsChanged } from "../whatsnew/WhatsNewModal";
// ============ LANGUAGE CONFIGURATION ============
// Language names stay in their native form regardless of UI language — standard picker convention
const LANGUAGES = {
  EN: 'en',
  ES: 'es',
  FR: 'fr',
  IT: 'it',
  JA: 'ja'
};

const LANGUAGE_INFO = {
  [LANGUAGES.EN]: { name: 'English' },
  [LANGUAGES.ES]: { name: 'Español' },
  [LANGUAGES.FR]: { name: 'Français' },
  [LANGUAGES.IT]: { name: 'Italiano' },
  [LANGUAGES.JA]: { name: '日本語' }
};

const ALL_LANGUAGES = Object.keys(LANGUAGE_INFO);

// Theme configuration
//
// These ids must match VALID_THEME_IDS in themeUtils. There is deliberately no
// AUTO entry: onboarding shows four concrete themes and picking one is an
// explicit manual choice, so Auto is turned off on save.
const THEMES = {
  CLASSIC: 'classic',
  DARK: 'comfort-dark',
  LIGHT: 'comfort-light',
  SKY: 'classic-light',
};

// English fallback names live here; actual displayed text translated at render via t()
//
// There are two families — Classic and Clean — each with a light and a dark
// member. Settings shows them in Light/Dark columns, so the labels there are
// just "Classic" and "Clean". This list is flat, so the mode is part of the
// label; without it the two Classics and the two Cleans would read identically.
//
// Insertion order drives ALL_THEMES and therefore the on-screen order: Classic
// Light leads, then the rest grouped by family. Icons carry mode, not family —
// Sun for the two light themes, Moon for the two dark ones.
const THEME_INFO = {

  [THEMES.SKY]: {
    name: 'Classic Light',
    icon: <Sun size={20} />,
    preview: { bg: 'linear-gradient(180deg, #f0f7ff 0%, #dbeafe 100%)', card: '#ffffff', text: '#1e293b', accent: '#5ed6f7', isGradient: true }
  },
  [THEMES.CLASSIC]: {
    name: 'Classic Dark',
    icon: <Moon size={20} />,
    preview: { bg: '#002031', card: '#01385a', text: '#ffffff', accent: '#016cae' }
  },
  [THEMES.LIGHT]: {
    name: 'Clean Light',
    icon: <Sun size={20} />,
    preview: { bg: '#F5F3EF', card: '#FFFEFA', text: '#2D2D2D', accent: '#016cae' }
  },
  [THEMES.DARK]: {
    name: 'Clean Dark',
    icon: <Moon size={20} />,
    preview: { bg: '#121212', card: '#1E1E1E', text: '#E8E8E8', accent: '#81C7F5' }
  },
};

const ALL_THEMES = Object.keys(THEME_INFO);

// Paint a theme without persisting it.
//
// themeUtils.applyTheme sets BOTH data-theme and data-mode; the token
// definitions and the [data-mode="..."] rules at the bottom of this file key
// off data-mode, so setting data-theme alone changes nothing visible. Passing a
// synthetic prefs object lets resolveTheme land on exactly the theme we want
// while leaving localStorage untouched — the choice is only committed in
// handleModalClose.
const previewTheme = (themeId) => {
  const side = getAutoSide(themeId);
  return applyTheme({
    mode: side,
    lightTheme: side === MODES.LIGHT ? themeId : DEFAULT_LIGHT_THEME,
    darkTheme: side === MODES.DARK ? themeId : DEFAULT_DARK_THEME
  });
};

// ============ FONT ============
// Font selection is no longer part of onboarding — it lives in Settings.
// Onboarding still applies whatever font is already stored so the modal renders
// in the user's chosen face, but it never writes the value.
const VALID_FONTS = ['system', 'verdana', 'atkinson'];
const DEFAULT_FONT = 'system';

// Maps the topic.name (English data) to the existing section_* translation key
// so translations in locale files are reused.
const TOPIC_TRANSLATION_KEY = {
  'Legislative': 'section_legislative',
  'Executive': 'section_executive',
  'Judicial': 'section_judicial',
  'Defense': 'section_defense',
  'Economy': 'section_economy',
  'Elections': 'section_elections',
  'Environment': 'section_environment',
  'Foreign Affairs': 'section_foreignAffairs',
  'Health': 'section_health',
  'Science & Tech': 'section_science',
  'Spending': 'section_spending',
  'Infrastructure': 'section_infrastructure',
  'Regulations': 'section_regulations',
  'Justice': 'section_justice',
};

// US States — state names stay English data per app-wide convention
const states = [
  { name: "Alabama", flag: "alabamaFlag.png", key: "alabama" },
  { name: "Alaska", flag: "alaskaFlag.png", key: "alaska" },
  { name: "Arizona", flag: "arizonaFlag.png", key: "arizona" },
  { name: "Arkansas", flag: "arkansasFlag.png", key: "arkansas" },
  { name: "California", flag: "californiaFlag.png", key: "california" },
  { name: "Colorado", flag: "coloradoFlag.png", key: "colorado" },
  { name: "Connecticut", flag: "connecticutFlag.png", key: "connecticut" },
  { name: "Delaware", flag: "delawareFlag.png", key: "delaware" },
  { name: "Florida", flag: "floridaFlag.png", key: "florida" },
  { name: "Georgia", flag: "georgiaFlag.png", key: "georgia" },
  { name: "Hawaii", flag: "hawaiiFlag.png", key: "hawaii" },
  { name: "Idaho", flag: "idahoFlag.png", key: "idaho" },
  { name: "Illinois", flag: "illinoisFlag.png", key: "illinois" },
  { name: "Indiana", flag: "indianaFlag.png", key: "indiana" },
  { name: "Iowa", flag: "iowaFlag.png", key: "iowa" },
  { name: "Kansas", flag: "kansasFlag.png", key: "kansas" },
  { name: "Kentucky", flag: "kentuckyFlag.png", key: "kentucky" },
  { name: "Louisiana", flag: "louisianaFlag.png", key: "louisiana" },
  { name: "Maine", flag: "maineFlag.png", key: "maine" },
  { name: "Maryland", flag: "marylandFlag.png", key: "maryland" },
  { name: "Massachusetts", flag: "massachusettsFlag.png", key: "massachusetts" },
  { name: "Michigan", flag: "michiganFlag.png", key: "michigan" },
  { name: "Minnesota", flag: "minnesotaFlag.png", key: "minnesota" },
  { name: "Mississippi", flag: "mississippiFlag.png", key: "mississippi" },
  { name: "Missouri", flag: "missouriFlag.png", key: "missouri" },
  { name: "Montana", flag: "montanaFlag.png", key: "montana" },
  { name: "Nebraska", flag: "nebraskaFlag.png", key: "nebraska" },
  { name: "Nevada", flag: "nevadaFlag.png", key: "nevada" },
  { name: "New Hampshire", flag: "newHampshireFlag.png", key: "newhampshire" },
  { name: "New Jersey", flag: "newJerseyFlag.png", key: "newjersey" },
  { name: "New Mexico", flag: "newMexicoFlag.png", key: "newmexico" },
  { name: "New York", flag: "newYorkFlag.png", key: "newyork" },
  { name: "North Carolina", flag: "northCarolinaFlag.png", key: "northcarolina" },
  { name: "North Dakota", flag: "northDakotaFlag.png", key: "northdakota" },
  { name: "Ohio", flag: "ohioFlag.png", key: "ohio" },
  { name: "Oklahoma", flag: "oklahomaFlag.png", key: "oklahoma" },
  { name: "Oregon", flag: "oregonFlag.png", key: "oregon" },
  { name: "Pennsylvania", flag: "pennsylvaniaFlag.png", key: "pennsylvania" },
  { name: "Rhode Island", flag: "rhodeIslandFlag.png", key: "rhodeisland" },
  { name: "South Carolina", flag: "southCarolinaFlag.png", key: "southcarolina" },
  { name: "South Dakota", flag: "southDakotaFlag.png", key: "southdakota" },
  { name: "Tennessee", flag: "tennesseeFlag.png", key: "tennessee" },
  { name: "Texas", flag: "texasFlag.png", key: "texas" },
  { name: "Utah", flag: "utahFlag.png", key: "utah" },
  { name: "Vermont", flag: "vermontFlag.png", key: "vermont" },
  { name: "Virginia", flag: "virginiaFlag.png", key: "virginia" },
  { name: "Washington", flag: "washingtonFlag.png", key: "washington" },
  { name: "West Virginia", flag: "westVirginiaFlag.png", key: "westvirginia" },
  { name: "Wisconsin", flag: "wisconsinFlag.png", key: "wisconsin" },
  { name: "Wyoming", flag: "wyomingFlag.png", key: "wyoming" }
];

const STATE_KEY_TO_ABBREV = {
  "alabama": "AL", "alaska": "AK", "arizona": "AZ", "arkansas": "AR", "california": "CA",
  "colorado": "CO", "connecticut": "CT", "delaware": "DE", "florida": "FL", "georgia": "GA",
  "hawaii": "HI", "idaho": "ID", "illinois": "IL", "indiana": "IN", "iowa": "IA",
  "kansas": "KS", "kentucky": "KY", "louisiana": "LA", "maine": "ME", "maryland": "MD",
  "massachusetts": "MA", "michigan": "MI", "minnesota": "MN", "mississippi": "MS",
  "missouri": "MO", "montana": "MT", "nebraska": "NE", "nevada": "NV", "newhampshire": "NH",
  "newjersey": "NJ", "newmexico": "NM", "newyork": "NY", "northcarolina": "NC",
  "northdakota": "ND", "ohio": "OH", "oklahoma": "OK", "oregon": "OR", "pennsylvania": "PA",
  "rhodeisland": "RI", "southcarolina": "SC", "southdakota": "SD", "tennessee": "TN",
  "texas": "TX", "utah": "UT", "vermont": "VT", "virginia": "VA", "washington": "WA",
  "westvirginia": "WV", "wisconsin": "WI", "wyoming": "WY"
};

// Topic names stay English here; render-site uses TOPIC_TRANSLATION_KEY map
const usTopics = [
  { name: "Legislative", icon: <Pen size={20} />, key: "LegislativeNews" },
  { name: "Executive", icon: <People size={20} />, key: "ExecutiveNews" },
  { name: "Judicial", icon: (<svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="currentColor"><path d="M160-120v-80h480v80H160Zm226-194L160-540l84-86 228 226-86 86Zm254-254L414-796l86-84 226 226-86 86Zm184 408L302-682l56-56 522 522-56 56Z"/></svg>), key: "JudicialNews" },
  { name: "Defense", icon: <ShieldShaded size={20} />, key: "DefenseNews" },
  { name: "Economy", icon: <Coin size={20} />, key: "EconomyNews" },
  { name: "Elections", icon: <ReceiptCutoff size={20} />, key: "ElectionNews" },
  { name: "Environment", icon: <Tree size={20} />, key: "EnvironmentNews" },
  { name: "Foreign Affairs", icon: <Globe2 size={20} />, key: "ForeignAffairsNews" },
  { name: "Health", icon: (<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-heart-pulse" viewBox="0 0 16 16"><path d="m8 2.748-.717-.737C5.6.281 2.514.878 1.4 3.053.918 3.995.78 5.323 1.508 7H.43c-2.128-5.697 4.165-8.83 7.394-5.857q.09.083.176.171a3 3 0 0 1 .176-.17c3.23-2.974 9.522.159 7.394 5.856h-1.078c.728-1.677.59-3.005.108-3.947C13.486.878 10.4.28 8.717 2.01zM2.212 10h1.315C4.593 11.183 6.05 12.458 8 13.795c1.949-1.337 3.407-2.612 4.473-3.795h1.315c-1.265 1.566-3.14 3.25-5.788 5-2.648-1.75-4.523-3.434-5.788-5"/><path d="M10.464 3.314a.5.5 0 0 0-.945.049L7.921 8.956 6.464 5.314a.5.5 0 0 0-.88-.091L3.732 8H.5a.5.5 0 0 0 0 1H4a.5.5 0 0 0 .416-.223l1.473-2.209 1.647 4.118a.5.5 0 0 0 .945-.049l1.598-5.593 1.457 3.642A.5.5 0 0 0 12 9h3.5a.5.5 0 0 0 0-1h-3.162z"/></svg>), key: "HealthNews" },
  { name: "Science & Tech", icon: (<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" className="bi bi-rocket-takeoff" viewBox="0 0 16 16"><path d="M9.752 6.193c.599.6 1.73.437 2.528-.362.798-.799.96-1.932.362-2.531-.599-.6-1.73-.438-2.528.361-.798.8-.96 1.933-.362 2.532Z"/><path d="M15.811 3.312c-.363 1.534-1.334 3.626-3.64 6.218l-.24 2.408a2.56 2.56 0 0 1-.732 1.526L8.817 15.85a.51.51 0 0 1-.867-.434l.27-1.899c.04-.28-.013-.593-.131-.956a9.42 9.42 0 0 0-.249-.657l-.082-.202c-.815-.197-1.578-.662-2.191-1.277-.614-.615-1.079-1.379-1.275-2.195l-.203-.083a9.556 9.556 0 0 0-.655-.248c-.363-.119-.675-.172-.955-.132l-1.896.27A.51.51 0 0 1 .15 7.17l2.382-2.386c.41-.41.947-.67 1.524-.734h.006l2.4-.238C9.005 1.55 11.087.582 12.623.208c.89-.217 1.59-.232 2.08-.188.244.023.435.06.57.093.067.017.12.033.16.045.184.06.279.13.351.295l.029.073a3.475 3.475 0 0 1 .157.721c.055.485.051 1.178-.159 2.065Zm-4.828 7.475.04-.04-.107 1.081a1.536 1.536 0 0 1-.44.913l-1.298 1.3.054-.38c.072-.506-.034-.993-.172-1.418a8.548 8.548 0 0 0-.164-.45c.738-.065 1.462-.38 2.087-1.006ZM5.205 5c-.625.626-.94 1.351-1.004 2.09a8.497 8.497 0 0 0-.45-.164c-.424-.138-.91-.244-1.416-.172l-.38.054 1.3-1.3c.245-.246.566-.401.91-.44l1.08-.107-.04.039Zm9.406-3.961c-.38-.034-.967-.027-1.746.163-1.558.38-3.917 1.496-6.937 4.521-.62.62-.799 1.34-.687 2.051.107.676.483 1.362 1.048 1.928.564.565 1.25.941 1.924 1.049.71.112 1.429-.067 2.048-.688 3.079-3.083 4.192-5.444 4.556-6.987.183-.771.18-1.345.138-1.713a2.835 2.835 0 0 0-.045-.283 3.078 3.078 0 0 0-.3-.041Z"/><path d="M7.009 12.139a7.632 7.632 0 0 1-1.804-1.352A7.568 7.568 0 0 1 3.794 8.86c-1.102.992-1.965 5.054-1.839 5.18.125.126 3.936-.896 5.054-1.902Z"/></svg>), key: "ScienceTechNews" },
  { name: "Spending", icon: (<svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="currentColor"><path d="M444-144v-80q-51-11-87.5-46T305-357l74-30q8 36 40.5 64.5T487-294q39 0 64-20t25-52q0-30-22.5-50T474-456q-78-28-114-61.5T324-604q0-50 32.5-86t87.5-47v-79h72v79q72 12 96.5 55t25.5 45l-70 29q-8-26-32-43t-53-17q-35 0-58 18t-23 44q0 26 25 44.5t93 41.5q70 23 102 60t32 94q0 57-37 96t-101 49v77h-72Z"/></svg>), key: "SpendingNews" },
  { name: "Infrastructure", icon: (<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16"><path d="M4 2.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5zm3 0a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5zm3.5-.5a.5.5 0 0 0-.5.5v1a.5.5 0 0 0 .5.5h1a.5.5 0 0 0 .5-.5v-1a.5.5 0 0 0-.5-.5z"/><path d="M2 1a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1zm11 0H3v14h3v-2.5a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 .5.5V15h3z"/></svg>), key: "InfrastructureNews" },
  { name: "Regulations", icon: (<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16"><path d="M5.5 7a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1zM5 9.5a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5m0 2a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 0 1h-2a.5.5 0 0 1-.5-.5"/><path d="M9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4.5zm0 1v2A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1z"/></svg>), key: "RegulationsNews" },
  { name: "Justice", icon: (<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16"><path d="M8 3a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3"/><path d="m5.93 6.704-.846 8.451a.768.768 0 0 0 1.523.203l.81-4.865a.59.59 0 0 1 1.165 0l.81 4.865a.768.768 0 0 0 1.523-.203l-.845-8.451A1.5 1.5 0 0 1 10.5 5.5L13 2.284a.796.796 0 0 0-1.239-.998L9.634 3.84a.7.7 0 0 1-.33.235c-.23.074-.665.176-1.304.176-.64 0-1.074-.102-1.305-.176a.7.7 0 0 1-.329-.235L4.239 1.286a.796.796 0 0 0-1.24.998l2.5 3.216c.317.316.475.758.43 1.204Z"/></svg>), key: "JusticeNews" }
];

const canadaTopics = [
  { name: "Legislative", icon: <Pen size={20} />, key: "CanadaLegislativeNews" },
  { name: "Executive", icon: <People size={20} />, key: "CanadaExecutiveNews" },
  { name: "Judicial", icon: (<svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="currentColor"><path d="M160-120v-80h480v80H160Zm226-194L160-540l84-86 228 226-86 86Zm254-254L414-796l86-84 226 226-86 86Zm184 408L302-682l56-56 522 522-56 56Z"/></svg>), key: "CanadaJudicialNews" },
  { name: "Defense", icon: <ShieldShaded size={20} />, key: "CanadaDefenseNews" },
  { name: "Economy", icon: <Coin size={20} />, key: "CanadaEconomyNews" },
  { name: "Elections", icon: <ReceiptCutoff size={20} />, key: "CanadaElectionsNews" },
  { name: "Environment", icon: <Tree size={20} />, key: "CanadaEnvironmentNews" },
  { name: "Foreign Affairs", icon: <Globe2 size={20} />, key: "CanadaForeignAffairsNews" },
  { name: "Health", icon: (<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-heart-pulse" viewBox="0 0 16 16"><path d="m8 2.748-.717-.737C5.6.281 2.514.878 1.4 3.053.918 3.995.78 5.323 1.508 7H.43c-2.128-5.697 4.165-8.83 7.394-5.857q.09.083.176.171a3 3 0 0 1 .176-.17c3.23-2.974 9.522.159 7.394 5.856h-1.078c.728-1.677.59-3.005.108-3.947C13.486.878 10.4.28 8.717 2.01zM2.212 10h1.315C4.593 11.183 6.05 12.458 8 13.795c1.949-1.337 3.407-2.612 4.473-3.795h1.315c-1.265 1.566-3.14 3.25-5.788 5-2.648-1.75-4.523-3.434-5.788-5"/><path d="M10.464 3.314a.5.5 0 0 0-.945.049L7.921 8.956 6.464 5.314a.5.5 0 0 0-.88-.091L3.732 8H.5a.5.5 0 0 0 0 1H4a.5.5 0 0 0 .416-.223l1.473-2.209 1.647 4.118a.5.5 0 0 0 .945-.049l1.598-5.593 1.457 3.642A.5.5 0 0 0 12 9h3.5a.5.5 0 0 0 0-1h-3.162z"/></svg>), key: "CanadaHealthNews" },
  { name: "Science & Tech", icon: (<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" className="bi bi-rocket-takeoff" viewBox="0 0 16 16"><path d="M9.752 6.193c.599.6 1.73.437 2.528-.362.798-.799.96-1.932.362-2.531-.599-.6-1.73-.438-2.528.361-.798.8-.96 1.933-.362 2.532Z"/><path d="M15.811 3.312c-.363 1.534-1.334 3.626-3.64 6.218l-.24 2.408a2.56 2.56 0 0 1-.732 1.526L8.817 15.85a.51.51 0 0 1-.867-.434l.27-1.899c.04-.28-.013-.593-.131-.956a9.42 9.42 0 0 0-.249-.657l-.082-.202c-.815-.197-1.578-.662-2.191-1.277-.614-.615-1.079-1.379-1.275-2.195l-.203-.083a9.556 9.556 0 0 0-.655-.248c-.363-.119-.675-.172-.955-.132l-1.896.27A.51.51 0 0 1 .15 7.17l2.382-2.386c.41-.41.947-.67 1.524-.734h.006l2.4-.238C9.005 1.55 11.087.582 12.623.208c.89-.217 1.59-.232 2.08-.188.244.023.435.06.57.093.067.017.12.033.16.045.184.06.279.13.351.295l.029.073a3.475 3.475 0 0 1 .157.721c.055.485.051 1.178-.159 2.065Zm-4.828 7.475.04-.04-.107 1.081a1.536 1.536 0 0 1-.44.913l-1.298 1.3.054-.38c.072-.506-.034-.993-.172-1.418a8.548 8.548 0 0 0-.164-.45c.738-.065 1.462-.38 2.087-1.006ZM5.205 5c-.625.626-.94 1.351-1.004 2.09a8.497 8.497 0 0 0-.45-.164c-.424-.138-.91-.244-1.416-.172l-.38.054 1.3-1.3c.245-.246.566-.401.91-.44l1.08-.107-.04.039Zm9.406-3.961c-.38-.034-.967-.027-1.746.163-1.558.38-3.917 1.496-6.937 4.521-.62.62-.799 1.34-.687 2.051.107.676.483 1.362 1.048 1.928.564.565 1.25.941 1.924 1.049.71.112 1.429-.067 2.048-.688 3.079-3.083 4.192-5.444 4.556-6.987.183-.771.18-1.345.138-1.713a2.835 2.835 0 0 0-.045-.283 3.078 3.078 0 0 0-.3-.041Z"/><path d="M7.009 12.139a7.632 7.632 0 0 1-1.804-1.352A7.568 7.568 0 0 1 3.794 8.86c-1.102.992-1.965 5.054-1.839 5.18.125.126 3.936-.896 5.054-1.902Z"/></svg>), key: "CanadaScienceTechNews" },
  { name: "Spending", icon: (<svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="currentColor"><path d="M444-144v-80q-51-11-87.5-46T305-357l74-30q8 36 40.5 64.5T487-294q39 0 64-20t25-52q0-30-22.5-50T474-456q-78-28-114-61.5T324-604q0-50 32.5-86t87.5-47v-79h72v79q72 12 96.5 55t25.5 45l-70 29q-8-26-32-43t-53-17q-35 0-58 18t-23 44q0 26 25 44.5t93 41.5q70 23 102 60t32 94q0 57-37 96t-101 49v77h-72Z"/></svg>), key: "CanadaSpendingNews" },
  { name: "Infrastructure", icon: (<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16"><path d="M4 2.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5zm3 0a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5zm3.5-.5a.5.5 0 0 0-.5.5v1a.5.5 0 0 0 .5.5h1a.5.5 0 0 0 .5-.5v-1a.5.5 0 0 0-.5-.5z"/><path d="M2 1a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1zm11 0H3v14h3v-2.5a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 .5.5V15h3z"/></svg>), key: "CanadaInfrastructureNews" },
  { name: "Regulations", icon: (<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16"><path d="M5.5 7a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1zM5 9.5a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5m0 2a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 0 1h-2a.5.5 0 0 1-.5-.5"/><path d="M9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4.5zm0 1v2A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1z"/></svg>), key: "CanadaRegulationsNews" },
  { name: "Justice", icon: (<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16"><path d="M8 3a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3"/><path d="m5.93 6.704-.846 8.451a.768.768 0 0 0 1.523.203l.81-4.865a.59.59 0 0 1 1.165 0l.81 4.865a.768.768 0 0 0 1.523-.203l-.845-8.451A1.5 1.5 0 0 1 10.5 5.5L13 2.284a.796.796 0 0 0-1.239-.998L9.634 3.84a.7.7 0 0 1-.33.235c-.23.074-.665.176-1.304.176-.64 0-1.074-.102-1.305-.176a.7.7 0 0 1-.329-.235L4.239 1.286a.796.796 0 0 0-1.24.998l2.5 3.216c.317.316.475.758.43 1.204Z"/></svg>), key: "CanadaJusticeNews" }
];

const mexicoTopics = [
  { name: "Legislative", icon: <Pen size={20} />, key: "MexicoLegislativeNews" },
  { name: "Executive", icon: <People size={20} />, key: "MexicoExecutiveNews" },
  { name: "Judicial", icon: (<svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="currentColor"><path d="M160-120v-80h480v80H160Zm226-194L160-540l84-86 228 226-86 86Zm254-254L414-796l86-84 226 226-86 86Zm184 408L302-682l56-56 522 522-56 56Z"/></svg>), key: "MexicoJudicialNews" },
  { name: "Defense", icon: <ShieldShaded size={20} />, key: "MexicoDefenseNews" },
  { name: "Economy", icon: <Coin size={20} />, key: "MexicoEconomyNews" },
  { name: "Environment", icon: <Tree size={20} />, key: "MexicoEnvironmentNews" },
  { name: "Foreign Affairs", icon: <Globe2 size={20} />, key: "MexicoForeignAffairsNews" },
  { name: "Health", icon: (<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-heart-pulse" viewBox="0 0 16 16"><path d="m8 2.748-.717-.737C5.6.281 2.514.878 1.4 3.053.918 3.995.78 5.323 1.508 7H.43c-2.128-5.697 4.165-8.83 7.394-5.857q.09.083.176.171a3 3 0 0 1 .176-.17c3.23-2.974 9.522.159 7.394 5.856h-1.078c.728-1.677.59-3.005.108-3.947C13.486.878 10.4.28 8.717 2.01zM2.212 10h1.315C4.593 11.183 6.05 12.458 8 13.795c1.949-1.337 3.407-2.612 4.473-3.795h1.315c-1.265 1.566-3.14 3.25-5.788 5-2.648-1.75-4.523-3.434-5.788-5"/><path d="M10.464 3.314a.5.5 0 0 0-.945.049L7.921 8.956 6.464 5.314a.5.5 0 0 0-.88-.091L3.732 8H.5a.5.5 0 0 0 0 1H4a.5.5 0 0 0 .416-.223l1.473-2.209 1.647 4.118a.5.5 0 0 0 .945-.049l1.598-5.593 1.457 3.642A.5.5 0 0 0 12 9h3.5a.5.5 0 0 0 0-1h-3.162z"/></svg>), key: "MexicoHealthNews" },
  { name: "Infrastructure", icon: (<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16"><path d="M4 2.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5zm3 0a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5zm3.5-.5a.5.5 0 0 0-.5.5v1a.5.5 0 0 0 .5.5h1a.5.5 0 0 0 .5-.5v-1a.5.5 0 0 0-.5-.5z"/><path d="M2 1a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1zm11 0H3v14h3v-2.5a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 .5.5V15h3z"/></svg>), key: "MexicoInfrastructureNews" }
];

const PROVINCE_KEYS = ["alberta","british_columbia","manitoba","new_brunswick","newfoundland_labrador","northwest_territories","nova_scotia","nunavut","ontario","prince_edward_island","quebec","saskatchewan","yukon"];

const ukTopics = [
  { name: "Legislative", icon: <Pen size={20} />, key: "UKLegislativeNews" },
  { name: "Executive", icon: <People size={20} />, key: "UKExecutiveNews" },
  { name: "Judicial", icon: (<svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="currentColor"><path d="M160-120v-80h480v80H160Zm226-194L160-540l84-86 228 226-86 86Zm254-254L414-796l86-84 226 226-86 86Zm184 408L302-682l56-56 522 522-56 56Z"/></svg>), key: "UKJudicialNews" },
  { name: "Defense", icon: <ShieldShaded size={20} />, key: "UKDefenseNews" },
  { name: "Economy", icon: <Coin size={20} />, key: "UKEconomyNews" },
  { name: "Environment", icon: <Tree size={20} />, key: "UKEnvironmentNews" },
  { name: "Foreign Affairs", icon: <Globe2 size={20} />, key: "UKForeignAffairsNews" },
  { name: "Health", icon: (<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-heart-pulse" viewBox="0 0 16 16"><path d="m8 2.748-.717-.737C5.6.281 2.514.878 1.4 3.053.918 3.995.78 5.323 1.508 7H.43c-2.128-5.697 4.165-8.83 7.394-5.857q.09.083.176.171a3 3 0 0 1 .176-.17c3.23-2.974 9.522.159 7.394 5.856h-1.078c.728-1.677.59-3.005.108-3.947C13.486.878 10.4.28 8.717 2.01zM2.212 10h1.315C4.593 11.183 6.05 12.458 8 13.795c1.949-1.337 3.407-2.612 4.473-3.795h1.315c-1.265 1.566-3.14 3.25-5.788 5-2.648-1.75-4.523-3.434-5.788-5"/><path d="M10.464 3.314a.5.5 0 0 0-.945.049L7.921 8.956 6.464 5.314a.5.5 0 0 0-.88-.091L3.732 8H.5a.5.5 0 0 0 0 1H4a.5.5 0 0 0 .416-.223l1.473-2.209 1.647 4.118a.5.5 0 0 0 .945-.049l1.598-5.593 1.457 3.642A.5.5 0 0 0 12 9h3.5a.5.5 0 0 0 0-1h-3.162z"/></svg>), key: "UKHealthNews" },
  { name: "Science & Tech", icon: (<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" className="bi bi-rocket-takeoff" viewBox="0 0 16 16"><path d="M9.752 6.193c.599.6 1.73.437 2.528-.362.798-.799.96-1.932.362-2.531-.599-.6-1.73-.438-2.528.361-.798.8-.96 1.933-.362 2.532Z"/><path d="M15.811 3.312c-.363 1.534-1.334 3.626-3.64 6.218l-.24 2.408a2.56 2.56 0 0 1-.732 1.526L8.817 15.85a.51.51 0 0 1-.867-.434l.27-1.899c.04-.28-.013-.593-.131-.956a9.42 9.42 0 0 0-.249-.657l-.082-.202c-.815-.197-1.578-.662-2.191-1.277-.614-.615-1.079-1.379-1.275-2.195l-.203-.083a9.556 9.556 0 0 0-.655-.248c-.363-.119-.675-.172-.955-.132l-1.896.27A.51.51 0 0 1 .15 7.17l2.382-2.386c.41-.41.947-.67 1.524-.734h.006l2.4-.238C9.005 1.55 11.087.582 12.623.208c.89-.217 1.59-.232 2.08-.188.244.023.435.06.57.093.067.017.12.033.16.045.184.06.279.13.351.295l.029.073a3.475 3.475 0 0 1 .157.721c.055.485.051 1.178-.159 2.065Zm-4.828 7.475.04-.04-.107 1.081a1.536 1.536 0 0 1-.44.913l-1.298 1.3.054-.38c.072-.506-.034-.993-.172-1.418a8.548 8.548 0 0 0-.164-.45c.738-.065 1.462-.38 2.087-1.006ZM5.205 5c-.625.626-.94 1.351-1.004 2.09a8.497 8.497 0 0 0-.45-.164c-.424-.138-.91-.244-1.416-.172l-.38.054 1.3-1.3c.245-.246.566-.401.91-.44l1.08-.107-.04.039Zm9.406-3.961c-.38-.034-.967-.027-1.746.163-1.558.38-3.917 1.496-6.937 4.521-.62.62-.799 1.34-.687 2.051.107.676.483 1.362 1.048 1.928.564.565 1.25.941 1.924 1.049.71.112 1.429-.067 2.048-.688 3.079-3.083 4.192-5.444 4.556-6.987.183-.771.18-1.345.138-1.713a2.835 2.835 0 0 0-.045-.283 3.078 3.078 0 0 0-.3-.041Z"/><path d="M7.009 12.139a7.632 7.632 0 0 1-1.804-1.352A7.568 7.568 0 0 1 3.794 8.86c-1.102.992-1.965 5.054-1.839 5.18.125.126 3.936-.896 5.054-1.902Z"/></svg>), key: "UKScienceTechNews" },
  { name: "Justice", icon: (<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16"><path d="M8 3a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3"/><path d="m5.93 6.704-.846 8.451a.768.768 0 0 0 1.523.203l.81-4.865a.59.59 0 0 1 1.165 0l.81 4.865a.768.768 0 0 0 1.523-.203l-.845-8.451A1.5 1.5 0 0 1 10.5 5.5L13 2.284a.796.796 0 0 0-1.239-.998L9.634 3.84a.7.7 0 0 1-.33.235c-.23.074-.665.176-1.304.176-.64 0-1.074-.102-1.305-.176a.7.7 0 0 1-.329-.235L4.239 1.286a.796.796 0 0 0-1.24.998l2.5 3.216c.317.316.475.758.43 1.204Z"/></svg>), key: "UKJusticeNews" },
  { name: "Infrastructure", icon: (<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16"><path d="M4 2.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5zm3 0a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5zm3.5-.5a.5.5 0 0 0-.5.5v1a.5.5 0 0 0 .5.5h1a.5.5 0 0 0 .5-.5v-1a.5.5 0 0 0-.5-.5z"/><path d="M2 1a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1zm11 0H3v14h3v-2.5a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 .5.5V15h3z"/></svg>), key: "UKInfrastructureNews" }
];

const MEXICAN_STATE_KEYS = ["aguascalientes","bajacalifornia","bajacaliforniasur","campeche","chiapas","chihuahua","coahuila","colima","durango","guanajuato","guerrero","hidalgo","jalisco","mexicocity","mexicostate","michoacan","morelos","nayarit","nuevoleon","oaxaca","puebla","queretaro","quintanaroo","sanluispotosi","sinaloa","sonora","tabasco","tamaulipas","tlaxcala","veracruz","yucatan","zacatecas"];

const UK_REGION_KEYS = ["england","scotland","wales","northernireland"];

const FULL_ONBOARDING_COUNTRIES = ['us', 'ca', 'mx', 'uk'];

// Country names stay English per app-wide convention
const ALL_COUNTRIES = [
  { code: 'us', name: 'United States', flag: '🇺🇸' },
  { code: 'ca', name: 'Canada', flag: '🇨🇦' },
  { code: 'mx', name: 'Mexico', flag: '🇲🇽' },
  { code: 'uk', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'australia', name: 'Australia', flag: '🇦🇺' },
  { code: 'france', name: 'France', flag: '🇫🇷' },
  { code: 'italy', name: 'Italy', flag: '🇮🇹' },
  { code: 'japan', name: 'Japan', flag: '🇯🇵' },
  { code: 'south-africa', name: 'South Africa', flag: '🇿🇦' },
  { code: 'ukraine', name: 'Ukraine', flag: '🇺🇦' }
];

// Theme is now the final step; font selection moved out of onboarding entirely.
const STEPS = { LANGUAGE: 1, COUNTRY: 2, REGION: 3, CITY: 4, TOPICS: 5, THEME: 6 };

const NotificationsOnboarding = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('common');
  const [show, setShow] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(1);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [homeStateValue, setHomeStateValue] = useState(null);
  const [homeProvinceValue, setHomeProvinceValue] = useState(null);
  const [homeMexicanStateValue, setHomeMexicanStateValue] = useState(null);
  const [homeUKRegionValue, setHomeUKRegionValue] = useState(null);
  const [homeCityValue, setHomeCityValue] = useState(null);
  const [isThemeOnlyMode, setIsThemeOnlyMode] = useState(false);
  const [isLanguageOnlyMode, setIsLanguageOnlyMode] = useState(false);
  // Seeded from the theme that is actually painted right now, so the matching
  // button is highlighted the moment the modal opens. resolveTheme handles the
  // Auto case and the legacy-storage migration for us.
  const [selectedTheme, setSelectedTheme] = useState(() => resolveTheme());
  // Whether the user actually picked a theme in this session. Nothing is
  // written to theme storage unless they did — otherwise the language-only and
  // topics-only flows would silently overwrite an existing Auto preference.
  const [themeTouched, setThemeTouched] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(LANGUAGES.EN);
  const [citySearchQuery, setCitySearchQuery] = useState("");
  const [citySearchResults, setCitySearchResults] = useState([]);
  const [searchingCities, setSearchingCities] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const searchTimeoutRef = React.useRef(null);
  const isNative = Capacitor.isNativePlatform();

  // Render-site helpers — keep config objects as English data; translate at point of display
  const translateTopicName = (name) => {
    const key = TOPIC_TRANSLATION_KEY[name];
    return key ? t(`common:${key}`, name) : name;
  };

  // One key per theme. The family alone isn't enough to key on here: Classic and
  // Clean each have a light and a dark member, and this flat list shows all four
  // at once, so the label has to carry the mode too.
  const translateThemeName = (themeKey) => {
    switch (themeKey) {
      case THEMES.SKY: return t('common:themeClassicLight', 'Classic Light');
      case THEMES.LIGHT: return t('common:themeCleanLight', 'Clean Light');
      case THEMES.CLASSIC: return t('common:themeClassicDark', 'Classic Dark');
      case THEMES.DARK: return t('common:themeCleanDark', 'Clean Dark');
      default: return THEME_INFO[themeKey]?.name;
    }
  };

  const getDefaultTopics = (country) => {
    if (country === 'ca') return ["CanadaLegislativeNews", "CanadaExecutiveNews", "CanadaJudicialNews"];
    if (country === 'mx') return ["MexicoLegislativeNews", "MexicoExecutiveNews", "MexicoJudicialNews"];
    if (country === 'uk') return ["UKLegislativeNews", "UKExecutiveNews", "UKJudicialNews"];
    return ["LegislativeNews", "ExecutiveNews", "JudicialNews"];
  };

  const [selectedTopics, setSelectedTopics] = useState([]);

  // Language is always step 1 and Country is always step 2; the remaining steps
  // depend on whether the selected country supports full onboarding / a city step.
  const getStepConfig = useCallback(() => {
    const showCityStep = selectedCountry === 'us' && isNative;
    const hasFullOnboarding = FULL_ONBOARDING_COUNTRIES.includes(selectedCountry);
    if (!hasFullOnboarding) {
      // Language(1) -> Country(2) -> Theme(3)
      return { showCityStep: false, regionStep: null, cityStep: null, topicsStep: null, themeStep: 3, maxSteps: 3 };
    }
    if (showCityStep) {
      // Language(1) -> Country(2) -> Region(3) -> City(4) -> Topics(5) -> Theme(6)
      return { showCityStep: true, regionStep: 3, cityStep: 4, topicsStep: 5, themeStep: 6, maxSteps: 6 };
    }
    // Language(1) -> Country(2) -> Region(3) -> Topics(4) -> Theme(5)
    return { showCityStep: false, regionStep: 3, cityStep: null, topicsStep: 4, themeStep: 5, maxSteps: 5 };
  }, [selectedCountry, isNative]);

  const searchCities = useCallback(async (query) => {
    if (!isNative) return;
    if (!query || query.trim().length < 2) {
      setCitySearchResults([]);
      setSearchError(null);
      setShowCityDropdown(false);
      return;
    }
    setSearchingCities(true);
    setSearchError(null);
    try {
      const response = await fetch(`https://search.govglance.org/api/geocode?search=${encodeURIComponent(query)}`);
      if (!response.ok) throw new Error('Failed to search cities');
      const data = await response.json();
      const validTypes = ['City', 'Town', 'Village', 'Borough', 'Township', 'Municipality', 'Locality'];
      const filteredResults = (data.results || []).filter(result => validTypes.includes(result.type));
      setCitySearchResults(filteredResults);
      setShowCityDropdown(filteredResults.length > 0);
    } catch (error) {
      console.error('City search error:', error);
      setSearchError(t('common:failedToSearchCities', 'Failed to search cities. Please try again.'));
      setCitySearchResults([]);
      setShowCityDropdown(false);
    } finally {
      setSearchingCities(false);
    }
  }, [isNative, t]);

  const handleCitySearchChange = useCallback((e) => {
    if (!isNative) return;
    const query = e.target.value;
    setCitySearchQuery(query);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => { searchCities(query); }, 300);
  }, [searchCities, isNative]);

  useEffect(() => {
    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, []);

  const provinces = useMemo(() => PROVINCE_KEYS.map((k) => {
    const info = getProvincialInfo(k);
    if (!info) return null;
    return { key: k, name: info.fullName, flag: info.flagImage, code: info.code, toggleKey: `${info.code}Updates` };
  }).filter(Boolean), []);

  const mexicanStates = useMemo(() => MEXICAN_STATE_KEYS.map((k) => {
    const info = getMexicanStateInfo(k);
    if (!info) return null;
    return { key: k, name: info.fullName, flag: info.flagImage, code: info.code, toggleKey: `${info.code}Updates` };
  }).filter(Boolean), []);

  const ukRegions = useMemo(() => UK_REGION_KEYS.map((k) => {
    const info = getUKRegionInfo(k);
    if (!info) return null;
    return { key: k, name: info.fullName, flag: info.flagImage, code: info.code, toggleKey: `${info.code}Updates` };
  }).filter(Boolean), []);

  useEffect(() => {
    const migrationResult = migrateOnboardingData();
    const onboarding = localStorage.getItem("onboarding");
    const themeOnboarding = localStorage.getItem("themeOnboarding");
    const homeCountry = localStorage.getItem("homeCountry");
    const homeState = localStorage.getItem("homeState");
    const homeProvince = localStorage.getItem("homeProvince");
    const homeMexicanState = localStorage.getItem("homeMexicanState");
    const homeUKRegion = localStorage.getItem("homeUKRegion");
    const homeCity = localStorage.getItem("homeCity");
    const languageOnboarding = localStorage.getItem("languageOnboarding");
    const savedLanguage = localStorage.getItem("gg_lang");

    if (savedLanguage && Object.values(LANGUAGES).includes(savedLanguage)) setSelectedLanguage(savedLanguage);
    else setSelectedLanguage(LANGUAGES.EN);

    if (homeCountry) {
      setSelectedCountry(homeCountry);
      const storedTopics = getStoredTopics(homeCountry);
      if (storedTopics.length > 0) setSelectedTopics(storedTopics);
      else setSelectedTopics(getDefaultTopics(homeCountry));
    } else if (migrationResult.detectedCountry) {
      setSelectedCountry(migrationResult.detectedCountry);
      if (migrationResult.migratedTopics.length > 0) setSelectedTopics(migrationResult.migratedTopics);
      else setSelectedTopics(getDefaultTopics(migrationResult.detectedCountry));
    }

    if (homeState) setHomeStateValue(homeState);
    else if (migrationResult.homeState) setHomeStateValue(migrationResult.homeState);

    if (homeProvince) setHomeProvinceValue(homeProvince);
    else if (migrationResult.homeProvince) setHomeProvinceValue(migrationResult.homeProvince);

    if (homeMexicanState) setHomeMexicanStateValue(homeMexicanState);
    else if (migrationResult.homeMexicanState) setHomeMexicanStateValue(migrationResult.homeMexicanState);

    if (homeUKRegion) setHomeUKRegionValue(homeUKRegion);
    else if (migrationResult.homeUKRegion) setHomeUKRegionValue(migrationResult.homeUKRegion);

    if (homeCity) setHomeCityValue(homeCity);

    if (onboarding !== "true") setShow(true);
    else if (themeOnboarding !== "true") { setShow(true); setIsThemeOnlyMode(true); }
    else if (languageOnboarding !== "true") { setShow(true); setIsLanguageOnlyMode(true); }
  }, [isNative]);

  // Paint whatever is already stored. themeUtils owns both attributes and the
  // legacy-key migration, so there is nothing to resolve here.
  useEffect(() => {
    applyStoredTheme();
  }, []);

  // Read-only: onboarding applies the stored font but no longer sets it
  useEffect(() => {
    const savedFont = localStorage.getItem("font");
    const fontToApply = savedFont && VALID_FONTS.includes(savedFont) ? savedFont : DEFAULT_FONT;
    document.documentElement.setAttribute('data-font', fontToApply);
    if (document.body) document.body.setAttribute('data-font', fontToApply);
  }, []);

  // Live preview of the highlighted theme. Only runs once the user has picked
  // something; until then the stored theme stays painted. No matchMedia listener
  // is needed because every option here is a concrete theme, never Auto.
  useEffect(() => {
    if (!themeTouched) return;
    previewTheme(selectedTheme);
  }, [selectedTheme, themeTouched]);

  useEffect(() => {
    if (i18n && selectedLanguage) i18n.changeLanguage(selectedLanguage);
  }, [selectedLanguage, i18n]);

  // Throw away an unsaved preview and go back to what is actually stored.
  const revertThemePreview = () => {
    if (themeTouched) applyStoredTheme();
  };

  const handleCountrySelected = (country) => {
    localStorage.setItem("homeCountry", country);
    setSelectedCountry(country);
    if (FULL_ONBOARDING_COUNTRIES.includes(country)) {
      const storedTopics = getStoredTopics(country);
      if (storedTopics.length > 0) setSelectedTopics(storedTopics);
      else setSelectedTopics(getDefaultTopics(country));
    }
    // Country(2) -> Region(3) for full onboarding, or Theme(3) for the rest
    setOnboardingStep(3);
  };

  const navigateToCountry = (country) => {
    const routes = { us: "/us", ca: "/canada", mx: "/mexico", uk: "/uk", australia: "/australia", france: "/france", italy: "/italy", japan: "/japan", "south-africa": "/south-africa", ukraine: "/ukraine" };
    navigate(routes[country] || "/");
  };

  const handleModalClose = async () => {
    // Picking a theme in onboarding is an explicit manual choice, so Auto goes
    // off first. selectTheme on its own would leave mode === 'auto' and the pick
    // would only take effect when the OS happened to agree.
    if (themeTouched) {
      setAutoEnabled(false);
      selectTheme(selectedTheme);
    }
    localStorage.setItem("themeOnboarding", "true");
    localStorage.setItem("gg_lang", selectedLanguage);
    localStorage.setItem("languageOnboarding", "true");
    notifyOnboardingFlagsChanged();
    if (i18n) i18n.changeLanguage(selectedLanguage);

    if (isLanguageOnlyMode) {
      window.dispatchEvent(new Event("storage"));
      setShow(false);
      navigateToCountry(localStorage.getItem("homeCountry") || "us");
      return;
    }
    if (isThemeOnlyMode) {
      window.dispatchEvent(new Event("storage"));
      setShow(false);
      navigateToCountry(localStorage.getItem("homeCountry") || "us");
      return;
    }

    selectedTopics.forEach((topicKey) => { localStorage.setItem(topicKey, "true"); });

    if (!isNative) {
      localStorage.setItem("onboarding", "true");
      window.dispatchEvent(new Event("storage"));
      setShow(false);
      navigateToCountry(selectedCountry);
      return;
    }

    try {
      const permResult = await PushNotifications.requestPermissions();
      if (permResult.receive !== 'granted') {
        localStorage.setItem("onboarding", "true");
        window.dispatchEvent(new Event("storage"));
        setShow(false);
        navigateToCountry(selectedCountry);
        return;
      }

      PushNotifications.addListener('registration', async (token) => {
        try {
          const platform = Capacitor.getPlatform();
          if (platform === 'android') await FCM.initialize();
          await FCM.getToken();
          const coreTopics = selectedCountry === 'ca'
            ? ['CanadaLegislativeNews', 'CanadaExecutiveNews', 'CanadaJudicialNews']
            : selectedCountry === 'mx'
            ? ['MexicoLegislativeNews', 'MexicoExecutiveNews', 'MexicoJudicialNews']
            : selectedCountry === 'uk'
            ? ['UKLegislativeNews', 'UKExecutiveNews', 'UKJudicialNews']
            : ['LegislativeNews', 'ExecutiveNews', 'JudicialNews'];
          for (const topicKey of selectedTopics) {
            if (coreTopics.includes(topicKey)) await FCM.subscribeTo({ topic: topicKey });
          }
        } catch (fcmError) { console.error('FCM setup failed:', fcmError); }
      });

      PushNotifications.addListener('registrationError', (error) => { console.error('Error on registration: ' + JSON.stringify(error)); });
      PushNotifications.addListener('pushNotificationReceived', (notification) => {});
      PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {});

      await PushNotifications.register();
    } catch (error) { console.error('Push notification setup failed:', error); }

    localStorage.setItem("onboarding", "true");
    window.dispatchEvent(new Event("storage"));
    setShow(false);
    navigateToCountry(selectedCountry);
  };

  const handleModalHeaderClose = () => {
    const { showCityStep, regionStep, cityStep, topicsStep, themeStep } = getStepConfig();
    if (isLanguageOnlyMode) { revertThemePreview(); setShow(false); return; }
    if (isThemeOnlyMode) {
      // Theme(1) -> Language(2)
      if (onboardingStep === 2) setOnboardingStep(1);
      else { revertThemePreview(); setShow(false); }
      return;
    }
    // Full onboarding: Language(1) -> Country(2) -> Region(3) -> [City] -> Topics -> Theme
    if (onboardingStep === themeStep) {
      if (topicsStep) setOnboardingStep(topicsStep);
      else setOnboardingStep(2); // non-full country: Theme(3) -> Country(2)
    } else if (onboardingStep === topicsStep) {
      if (showCityStep) setOnboardingStep(cityStep);
      else setOnboardingStep(regionStep);
    } else if (showCityStep && onboardingStep === cityStep) setOnboardingStep(regionStep);
    else if (onboardingStep === regionStep) setOnboardingStep(2);
    else if (onboardingStep === 2) setOnboardingStep(1);
  };

  const handleStateSelected = (stateKey) => {
    const { showCityStep, cityStep, topicsStep } = getStepConfig();
    localStorage.setItem("homeState", stateKey);
    localStorage.setItem(stateKey, "true");
    setHomeStateValue(stateKey);
    setSelectedTopics((prev) => prev.includes(stateKey) ? prev : [...prev, stateKey]);
    if (showCityStep) {
      setCitySearchQuery("");
      setCitySearchResults([]);
      setShowCityDropdown(false);
      setHomeCityValue(null);
      setOnboardingStep(cityStep);
    } else {
      setOnboardingStep(topicsStep);
    }
  };

  const handleCitySelected = (city) => {
    const { topicsStep } = getStepConfig();
    if (!isNative) return;
    localStorage.setItem("homeCity", city.name);
    localStorage.setItem("homeCityState", city.state);
    localStorage.setItem("homeCityStateName", city.stateName);
    localStorage.setItem("homeCityData", JSON.stringify(city));
    setHomeCityValue(`${city.name}, ${city.state}`);
    setShowCityDropdown(false);
    setCitySearchQuery(`${city.name}, ${city.stateName}`);
    if (!homeStateValue && city.state) {
      const stateKey = Object.keys(STATE_KEY_TO_ABBREV).find(key => STATE_KEY_TO_ABBREV[key] === city.state);
      if (stateKey) {
        localStorage.setItem("homeState", stateKey);
        localStorage.setItem(stateKey, "true");
        setHomeStateValue(stateKey);
        setSelectedTopics((prev) => prev.includes(stateKey) ? prev : [...prev, stateKey]);
      }
    }
    setOnboardingStep(topicsStep);
  };

  const handleSkipCity = () => {
    const { topicsStep } = getStepConfig();
    setOnboardingStep(topicsStep);
  };

  const handleSkipRegion = () => {
    const { showCityStep, cityStep, topicsStep } = getStepConfig();
    if (showCityStep) setOnboardingStep(cityStep);
    else setOnboardingStep(topicsStep);
  };

  const handleProvinceSelected = (provinceKey) => {
    const { topicsStep } = getStepConfig();
    const info = getProvincialInfo(provinceKey);
    if (!info) return;
    const toggleKey = `${info.code}Updates`;
    localStorage.setItem("homeProvince", provinceKey);
    localStorage.setItem(toggleKey, "true");
    setHomeProvinceValue(provinceKey);
    setSelectedTopics((prev) => prev.includes(toggleKey) ? prev : [...prev, toggleKey]);
    setOnboardingStep(topicsStep);
  };

  const handleMexicanStateSelected = (stateKey) => {
    const { topicsStep } = getStepConfig();
    const info = getMexicanStateInfo(stateKey);
    if (!info) return;
    const toggleKey = `${info.code}Updates`;
    localStorage.setItem("homeMexicanState", stateKey);
    localStorage.setItem(toggleKey, "true");
    setHomeMexicanStateValue(stateKey);
    setSelectedTopics((prev) => prev.includes(toggleKey) ? prev : [...prev, toggleKey]);
    setOnboardingStep(topicsStep);
  };

  const handleUKRegionSelected = (regionKey) => {
    const { topicsStep } = getStepConfig();
    const info = getUKRegionInfo(regionKey);
    if (!info) return;
    const toggleKey = `${info.code}Updates`;
    localStorage.setItem("homeUKRegion", regionKey);
    localStorage.setItem(toggleKey, "true");
    setHomeUKRegionValue(regionKey);
    setSelectedTopics((prev) => prev.includes(toggleKey) ? prev : [...prev, toggleKey]);
    setOnboardingStep(topicsStep);
  };

  const handleTopicToggle = (topicKey) => {
    setSelectedTopics((prev) => prev.includes(topicKey) ? prev.filter((k) => k !== topicKey) : [...prev, topicKey]);
  };

  const handleTopicsContinue = () => {
    const { themeStep } = getStepConfig();
    setOnboardingStep(themeStep);
  };

  const handleThemeSelected = (theme) => {
    setThemeTouched(true);
    setSelectedTheme(theme);
  };

  const handleLanguageSelected = (lang) => { setSelectedLanguage(lang); };

  const handleLanguageContinue = () => {
    // Full onboarding: Language(1) -> Country(2)
    setOnboardingStep(2);
  };

  // Region-name fallbacks use translated "Selected X" labels
  const formatRegionName = () => {
    if (selectedCountry === "ca" && homeProvinceValue) {
      const info = getProvincialInfo(homeProvinceValue);
      return info ? info.fullName : t('common:selectedProvinceTerritory', 'Selected Province/Territory');
    }
    if (selectedCountry === "us" && homeStateValue) {
      const state = states.find((s) => s.key === homeStateValue);
      return state ? state.name : t('common:selectedStateLabel', 'Selected State');
    }
    if (selectedCountry === "mx" && homeMexicanStateValue) {
      const info = getMexicanStateInfo(homeMexicanStateValue);
      return info ? info.fullName : t('common:selectedStateLabel', 'Selected State');
    }
    if (selectedCountry === "uk" && homeUKRegionValue) {
      const info = getUKRegionInfo(homeUKRegionValue);
      return info ? info.fullName : t('common:selectedRegionLabel', 'Selected Region');
    }
    return t('common:selectedRegionLabel', 'Selected Region');
  };

  const getRegionFlag = () => {
    if (selectedCountry === "ca" && homeProvinceValue) { const info = getProvincialInfo(homeProvinceValue); return info ? info.flagImage : null; }
    if (selectedCountry === "us" && homeStateValue) { const state = states.find((s) => s.key === homeStateValue); return state ? state.flag : null; }
    if (selectedCountry === "mx" && homeMexicanStateValue) { const info = getMexicanStateInfo(homeMexicanStateValue); return info ? info.flagImage : null; }
    if (selectedCountry === "uk" && homeUKRegionValue) { const info = getUKRegionInfo(homeUKRegionValue); return info ? info.flagImage : null; }
    return null;
  };

  const getFontSize = (name) => {
    if (!name) return "19px";
    if (name.length > 12) return "15px";
    if (name.length > 9) return "17px";
    return "19px";
  };

  const renderThemeOption = (themeKey) => {
    const info = THEME_INFO[themeKey];
    const isSelected = selectedTheme === themeKey;
    const isGradient = info.preview.isGradient;
    return (
      <button key={themeKey} onClick={() => handleThemeSelected(themeKey)} className={`onboarding-theme-btn ${isSelected ? 'selected' : ''}`}>
        <div className="onboarding-theme-preview" style={{ background: isGradient ? info.preview.bg : undefined, backgroundColor: !isGradient ? info.preview.bg : undefined }}>
          <div className="onboarding-theme-preview-card" style={{ background: isGradient ? info.preview.card : undefined, backgroundColor: !isGradient ? info.preview.card : undefined }}>
            <div className="onboarding-theme-preview-header" style={{ backgroundColor: info.preview.accent }} />
            <div className="onboarding-theme-preview-content">
              <div className="onboarding-theme-preview-line full" style={{ backgroundColor: info.preview.text }} />
              <div className="onboarding-theme-preview-line short" style={{ backgroundColor: info.preview.text }} />
            </div>
          </div>
        </div>
        <div className="onboarding-theme-info">
          <div className="onboarding-theme-header">
            <span className="onboarding-theme-icon">{info.icon}</span>
            <span className="onboarding-theme-name">{translateThemeName(themeKey)}</span>
            {isSelected && <CheckCircleFill className="onboarding-theme-check" />}
          </div>
        </div>
      </button>
    );
  };

  const currentTopics = selectedCountry === "ca" ? canadaTopics : selectedCountry === "mx" ? mexicoTopics : selectedCountry === "uk" ? ukTopics : usTopics;
  const currentRegions = selectedCountry === "ca" ? provinces : selectedCountry === "mx" ? mexicanStates : selectedCountry === "uk" ? ukRegions : states;

  // Region step title — one key per region type since article/gender varies by language
  const regionStepTitle = selectedCountry === "ca"
    ? t('common:chooseAProvinceTerritory', 'Choose a Province/Territory')
    : selectedCountry === "uk"
    ? t('common:chooseARegion', 'Choose a Region')
    : t('common:chooseAState', 'Choose a State');

  const { showCityStep, regionStep, cityStep, topicsStep, themeStep } = getStepConfig();
  const hasFullOnboarding = FULL_ONBOARDING_COUNTRIES.includes(selectedCountry);

  let isCountryStep = false;
  let isRegionStep = false;
  let isCityStep = false;
  let isTopicsStep = false;
  let isThemeStep = false;
  let isLanguageStep = false;

  if (isLanguageOnlyMode) {
    isLanguageStep = onboardingStep === 1;
  } else if (isThemeOnlyMode) {
    isThemeStep = onboardingStep === 1;
    isLanguageStep = onboardingStep === 2;
  } else {
    isLanguageStep = onboardingStep === 1;
    isCountryStep = onboardingStep === 2;
    isRegionStep = onboardingStep === regionStep && hasFullOnboarding;
    isCityStep = onboardingStep === cityStep && showCityStep;
    isTopicsStep = onboardingStep === topicsStep;
    isThemeStep = onboardingStep === themeStep;
  }

  if (!show) return null;

  return (
    <>
      <style>{`
        .onboarding-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background-color: rgba(0, 0, 0, 0.5); display: flex; justify-content: center; align-items: center; z-index: 1000; }
        .onboarding-modal { background-color: var(--bg-primary); border-radius: 8px; width: 90%; max-width: 900px; max-height: 95vh; min-height: 95vh; overflow: hidden; color: var(--text-primary); display: flex; flex-direction: column; transition: background-color 0.3s ease, color 0.3s ease; }
        .onboarding-logo-header { padding-top: max(env(safe-area-inset-top, 20px), 40px); padding-bottom: 20px; padding-left: 20px; padding-right: 20px; display: flex; justify-content: center; align-items: center; flex-shrink: 0; }
        .onboarding-logo-header.theme-only { padding-top: 20px; }
        .onboarding-step-header { padding: 15px 20px; display: flex; align-items: center; justify-content: center; position: relative; flex-shrink: 0; }
        .onboarding-back-btn { position: absolute; left: 20px; background: none; border: none; color: var(--text-primary); cursor: pointer; display: flex; align-items: center; }
        .onboarding-step-title { margin: 0; font-size: 1.25rem; color: var(--text-primary); }
        .onboarding-skip-btn { position: absolute; right: 20px; font-size: 12px; padding: 4px 12px; background-color: var(--bg-button-secondary); color: var(--text-primary); border: none; border-radius: 15px; cursor: pointer; }
        .onboarding-content { padding: 20px; flex: 1; overflow: hidden; display: flex; flex-direction: column; }
        .onboarding-country-list { display: flex; flex-direction: column; gap: 15px; align-items: center; padding: 20px; flex: 1; overflow-y: auto; }
        .onboarding-country-btn { font-size: 20px; display: block; width: 80%; margin-bottom: 15px; background-color: var(--bg-button-accent); color: var(--text-primary); border: none; border-radius: 25px; cursor: pointer; box-shadow: 2px 2px 12px var(--shadow-heavy); padding: 10px; text-decoration: none; }
        .onboarding-region-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px; }
        .onboarding-region-btn { font-size: 17px; display: flex; align-items: center; justify-content: center; min-height: 60px; padding: 10px; background-color: var(--bg-button-accent); color: var(--text-primary); border: none; border-radius: 25px; cursor: pointer; box-shadow: 2px 2px 12px var(--shadow-heavy); }
        .onboarding-region-btn.selected { background-color: var(--accent-secondary); border: 2px solid var(--text-primary); }
        .onboarding-region-btn img { height: 24px; margin-right: 8px; object-fit: contain; }
        .onboarding-city-search { flex: 1; display: flex; flex-direction: column; }
        .onboarding-city-input-wrapper { position: relative; margin-bottom: 20px; }
        .onboarding-city-input { width: 100%; padding: 15px 20px; font-size: 16px; background-color: var(--search-bg); color: var(--text-primary); border: 1px solid var(--border-primary); border-radius: 15px; outline: none; transition: all 0.2s ease; }
        .onboarding-city-input.dropdown-open { border-radius: 15px 15px 0 0; }
        .onboarding-city-input::placeholder { color: var(--text-muted); }
        .onboarding-search-loading { position: absolute; right: 15px; top: 50%; transform: translateY(-50%); color: var(--text-muted); font-size: 14px; }
        .onboarding-city-dropdown { position: absolute; top: 100%; left: 0; right: 0; max-height: 400px; overflow-y: auto; background-color: var(--search-bg); border: 1px solid var(--border-primary); border-top: none; border-radius: 0 0 15px 15px; z-index: 100; box-shadow: 0 4px 12px var(--shadow-color); }
        .onboarding-city-option { width: 100%; padding: 12px 20px; font-size: 14px; background-color: transparent; color: var(--text-primary); border: none; border-bottom: 1px solid var(--border-secondary); cursor: pointer; text-align: left; display: flex; justify-content: space-between; align-items: center; transition: background-color 0.2s ease; }
        .onboarding-city-option:last-child { border-bottom: none; }
        .onboarding-city-option:hover { background-color: var(--bg-card-hover); }
        .onboarding-error-msg { margin-bottom: 15px; padding: 10px; background-color: var(--color-error-bg); border-radius: 10px; text-align: center; color: var(--color-error); }
        .onboarding-success-msg { margin-bottom: 15px; padding: 15px; background-color: var(--color-success-bg); border-radius: 10px; text-align: center; color: var(--color-success); }
        .onboarding-helper-text { text-align: center; color: var(--text-muted); font-size: 14px; flex-grow: 1; display: flex; align-items: center; justify-content: center; }
        .onboarding-topics-container { display: flex; flex-direction: column; flex: 1; overflow: hidden; }
        .onboarding-region-display { margin-bottom: 15px; flex-shrink: 0; }
        .onboarding-region-display-btn { width: 100%; font-size: 17px; display: flex; align-items: center; justify-content: center; min-height: 50px; padding: 8px; background-color: var(--bg-button-accent); color: var(--text-primary); border: none; border-radius: 25px; opacity: 1; }
        .onboarding-region-display-btn img { height: 22px; margin-right: 8px; object-fit: contain; }
        .onboarding-topics-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; flex: 1; overflow-y: auto; padding-bottom: 10px; align-content: start; }
        .onboarding-topic-btn { font-size: 14px; display: flex; align-items: center; justify-content: center; min-height: 50px; max-height: 50px; padding: 8px 5px; background-color: var(--bg-button-secondary); opacity: 0.7; color: var(--text-primary); border: none; border-radius: 25px; cursor: pointer; box-shadow: 2px 2px 12px var(--shadow-heavy); transition: all 0.2s ease; }
        .onboarding-topic-btn.selected { background-color: var(--bg-button-accent); opacity: 1; }
        .onboarding-topic-btn span { margin-right: 6px; }
        .onboarding-continue-wrapper { flex-shrink: 0; padding-top: 15px; padding-bottom: 10px; }
        .onboarding-continue-btn { width: 75%; margin: 0 auto; display: block; font-size: 18px; min-height: 45px; padding: 10px; background-color: var(--bg-button-accent); color: var(--text-primary); border: none; border-radius: 25px; cursor: pointer; box-shadow: 2px 2px 12px var(--shadow-heavy); }
        .onboarding-theme-container { display: flex; flex-direction: column; flex: 1; overflow: hidden; }
        .onboarding-theme-description { text-align: center; margin-bottom: 15px; color: var(--text-muted); font-size: 14px; }
        .onboarding-theme-list { display: flex; flex-direction: column; gap: 12px; width: 100%; max-width: 400px; margin: 0 auto; flex: 1; overflow-y: auto; padding-bottom: 10px; }
        .onboarding-theme-btn { display: flex; align-items: center; padding: 12px; background-color: var(--bg-button-secondary); border: 2px solid transparent; border-radius: 16px; cursor: pointer; transition: all 0.2s ease; width: 100%; }
        .onboarding-theme-btn:hover { background-color: var(--bg-card-hover); }
        .onboarding-theme-btn.selected { background-color: var(--onboarding-theme-button-selected-bg, var(--bg-button-accent)); border-color: var(--onboarding-theme-button-border, var(--accent-hover)); box-shadow: 0 0 20px var(--accent-glow); }
        .onboarding-theme-preview { width: 70px; height: 50px; border-radius: 10px; border: 1px solid var(--border-light); display: flex; align-items: center; justify-content: center; margin-right: 12px; flex-shrink: 0; overflow: hidden; }
        .onboarding-theme-preview-card { width: 50px; height: 35px; border-radius: 6px; overflow: hidden; }
        .onboarding-theme-preview-header { height: 8px; width: 100%; }
        .onboarding-theme-preview-content { padding: 5px; display: flex; flex-direction: column; gap: 3px; }
        .onboarding-theme-preview-line { height: 3px; border-radius: 2px; }
        .onboarding-theme-preview-line.full { width: 100%; opacity: 0.7; }
        .onboarding-theme-preview-line.short { width: 60%; opacity: 0.5; }
        .onboarding-theme-info { flex: 1; text-align: left; min-width: 0; }
        .onboarding-theme-header { display: flex; align-items: center; gap: 8px; }
        .onboarding-theme-icon { display: flex; align-items: center; color: var(--accent-primary); }
        .onboarding-theme-name { font-size: 16px; font-weight: 600; color: var(--text-primary); }
        .onboarding-theme-check { margin-left: auto; color: var(--accent-hover); font-size: 16px; }
        .onboarding-finish-wrapper { flex-shrink: 0; padding-top: 20px; padding-bottom: 10px; }
        .onboarding-finish-btn { width: 75%; margin: 0 auto; display: block; font-size: 18px; min-height: 45px; padding: 10px; background-color: var(--accent-primary); color: white; border: none; border-radius: 25px; cursor: pointer; box-shadow: 2px 2px 12px var(--shadow-heavy); }
        .onboarding-finish-btn:hover { background-color: var(--accent-secondary); }
        .onboarding-option-container { display: flex; flex-direction: column; flex: 1; overflow: hidden; }
        .onboarding-option-list { display: flex; flex-direction: column; gap: 12px; width: 100%; max-width: 400px; margin: 0 auto; flex: 1; overflow-y: auto; padding-bottom: 10px; }
        .onboarding-option-btn { display: flex; align-items: center; padding: 16px 20px; background-color: var(--bg-button-secondary); border: 2px solid transparent; border-radius: 16px; cursor: pointer; transition: all 0.2s ease; width: 100%; text-align: left; }
        .onboarding-option-btn:hover { background-color: var(--bg-card-hover); }
        .onboarding-option-btn.selected { background-color: var(--onboarding-theme-button-selected-bg, var(--bg-button-accent)); border-color: var(--onboarding-theme-button-border, var(--accent-hover)); box-shadow: 0 0 15px var(--accent-glow); }
        .onboarding-option-name { font-size: 18px; font-weight: 500; color: var(--text-primary); flex: 1; }
        .onboarding-option-check { margin-left: auto; color: var(--accent-hover); font-size: 18px; }
        [data-mode="light"] .onboarding-theme-icon { color: var(--accent-primary); }
        [data-mode="light"] .onboarding-theme-check, [data-mode="light"] .onboarding-option-check { color: var(--accent-primary); }
        [data-mode="light"] .onboarding-theme-btn.selected, [data-mode="light"] .onboarding-option-btn.selected { background: rgba(1, 108, 174, 0.1); border-color: var(--accent-primary); box-shadow: 0 0 15px rgba(1, 108, 174, 0.15); }
        [data-mode="dark"] .onboarding-theme-icon { color: var(--accent-primary); }
        [data-mode="dark"] .onboarding-theme-check, [data-mode="dark"] .onboarding-option-check { color: var(--accent-primary); }
        [data-mode="dark"] .onboarding-theme-btn.selected, [data-mode="dark"] .onboarding-option-btn.selected { background: rgba(129, 199, 245, 0.15); border-color: var(--accent-primary); box-shadow: 0 0 15px rgba(129, 199, 245, 0.15); }
      `}</style>

      <div id='homebg'>
        <div className="onboarding-overlay">
          <div className="onboarding-modal">
            <div className={`onboarding-logo-header ${(isThemeOnlyMode || isLanguageOnlyMode) ? 'theme-only' : ''}`}>
              <Image width='150px' src='favicon.ico' />
            </div>

            <div className="onboarding-step-header">
              {onboardingStep > 1 && (
                <button onClick={handleModalHeaderClose} className="onboarding-back-btn">
                  <CaretLeftFill size={20} />
                </button>
              )}
              <h5 className="onboarding-step-title">
                {isLanguageStep && (<><Globe2 /> {t('common:chooseYourLanguage', 'Choose Your Language')}</>)}
                {isCountryStep && (<><Globe2 /> {t('common:chooseYourCountry', 'Choose Your Country')}</>)}
                {isRegionStep && (<><GeoAlt /> {regionStepTitle}</>)}
                {isCityStep && (<><Building /> {t('common:selectYourCity', 'Select Your City')}</>)}
                {isTopicsStep && <>{t('common:selectTopicsToFollow', 'Select Topics to Follow')}</>}
                {isThemeStep && (<><Palette /> {t('common:chooseYourTheme', 'Choose Your Theme')}</>)}
              </h5>
              {(isRegionStep || isCityStep) && (
                <button onClick={isRegionStep ? handleSkipRegion : handleSkipCity} className="onboarding-skip-btn">
                  {t('common:skip', 'Skip')}
                </button>
              )}
            </div>

            <div className="onboarding-content">
              {/* Step 1: Language Selection */}
              {isLanguageStep && (
                <div className="onboarding-option-container">
                  <div className="onboarding-option-list">
                    {ALL_LANGUAGES.map((langKey) => {
                      const info = LANGUAGE_INFO[langKey];
                      const isSelected = selectedLanguage === langKey;
                      return (
                        <button
                          key={langKey}
                          onClick={() => handleLanguageSelected(langKey)}
                          className={`onboarding-option-btn ${isSelected ? 'selected' : ''}`}
                        >
                          <span className="onboarding-option-name">{info.name}</span>
                          {isSelected && <CheckCircleFill className="onboarding-option-check" />}
                        </button>
                      );
                    })}
                  </div>
                  {(isThemeOnlyMode || isLanguageOnlyMode) ? (
                    <div className="onboarding-finish-wrapper">
                      <button onClick={handleModalClose} className="onboarding-finish-btn">
                        {t('common:savePreferences', 'Save Preferences')}
                      </button>
                    </div>
                  ) : (
                    <div className="onboarding-continue-wrapper">
                      <button onClick={handleLanguageContinue} className="onboarding-continue-btn">
                        {t('common:continue', 'Continue')}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Step 2: Country Selection */}
              {isCountryStep && (
                <div className="onboarding-country-list">
                  {ALL_COUNTRIES.map((country) => (
                    <button key={country.code} onClick={() => handleCountrySelected(country.code)} className="onboarding-country-btn">
                      {country.flag} {country.name}
                    </button>
                  ))}
                </div>
              )}

              {/* Step 3: Region Selection */}
              {isRegionStep && (
                <div style={{ flex: 1, overflowY: "auto" }}>
                  <div className="onboarding-region-grid">
                    {currentRegions.map((region) => {
                      const isSelected =
                        (selectedCountry === 'us' && homeStateValue === region.key) ||
                        (selectedCountry === 'ca' && homeProvinceValue === region.key) ||
                        (selectedCountry === 'mx' && homeMexicanStateValue === region.key) ||
                        (selectedCountry === 'uk' && homeUKRegionValue === region.key);
                      return (
                        <button
                          key={region.key}
                          onClick={() => {
                            if (selectedCountry === "ca") handleProvinceSelected(region.key);
                            else if (selectedCountry === "mx") handleMexicanStateSelected(region.key);
                            else if (selectedCountry === "uk") handleUKRegionSelected(region.key);
                            else handleStateSelected(region.key);
                          }}
                          className={`onboarding-region-btn ${isSelected ? 'selected' : ''}`}
                          style={{ fontSize: getFontSize(region.name) }}
                        >
                          <img src={region.flag} alt={`${region.name} flag`} />
                          {region.name}
                          {isSelected && " ✓"}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Step 4: City Selection (US only, native only) */}
              {isCityStep && (
                <div className="onboarding-city-search">
                  <div className="onboarding-city-input-wrapper">
                    <input
                      type="text"
                      value={citySearchQuery}
                      onChange={handleCitySearchChange}
                      onFocus={() => citySearchResults.length > 0 && setShowCityDropdown(true)}
                      placeholder={t('common:typeCityName', 'Type your city name...')}
                      className={`onboarding-city-input ${showCityDropdown ? 'dropdown-open' : ''}`}
                    />
                    {searchingCities && (
                      <div className="onboarding-search-loading">
                        {t('common:searching', 'Searching...')}
                      </div>
                    )}
                    {showCityDropdown && !searchingCities && citySearchResults.length > 0 && (
                      <div className="onboarding-city-dropdown">
                        {citySearchResults.map((city, index) => (
                          <button key={`${city.name}_${city.state}_${index}`} onClick={() => handleCitySelected(city)} className="onboarding-city-option">
                            <span>
                              <span style={{ fontWeight: "500" }}>{city.name}</span>
                              <span style={{ opacity: 0.7 }}>, {city.stateName}</span>
                            </span>
                            <span style={{ fontSize: "12px", opacity: 0.5 }}>{city.type}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {searchError && (
                    <div className="onboarding-error-msg">{searchError}</div>
                  )}

                  {homeCityValue && (
                    <div className="onboarding-success-msg">
                      {t('common:currentlySelectedCity', 'Currently selected: {{city}}', { city: homeCityValue })}
                    </div>
                  )}

                  <div className="onboarding-helper-text">
                    {citySearchQuery.length < 2 ? (
                      <p>{t('common:startTypingCity', 'Start typing to search for your city')}</p>
                    ) : !searchingCities && citySearchResults.length === 0 && citySearchQuery.length >= 2 ? (
                      <p>{t('common:noCitiesFoundForQuery', 'No cities found matching "{{query}}"', { query: citySearchQuery })}</p>
                    ) : null}
                  </div>
                </div>
              )}

              {/* Topics Selection Step */}
              {isTopicsStep && (
                <div className="onboarding-topics-container">
                  {((selectedCountry === 'us' && homeStateValue) ||
                    (selectedCountry === 'ca' && homeProvinceValue) ||
                    (selectedCountry === 'mx' && homeMexicanStateValue) ||
                    (selectedCountry === 'uk' && homeUKRegionValue)) && (
                    <div className="onboarding-region-display">
                      <button disabled className="onboarding-region-display-btn">
                        {getRegionFlag() && (
                          <img src={getRegionFlag()} alt={`${formatRegionName()} flag`} />
                        )}
                        {formatRegionName()}
                        {homeCityValue && isNative && (
                          <span style={{ marginLeft: "8px", fontSize: "13px", opacity: 0.8 }}>• {homeCityValue}</span>
                        )}
                      </button>
                    </div>
                  )}

                  <div className="onboarding-topics-grid">
                    {currentTopics.map((topic) => (
                      <button
                        key={topic.key}
                        onClick={() => handleTopicToggle(topic.key)}
                        className={`onboarding-topic-btn ${selectedTopics.includes(topic.key) ? 'selected' : ''}`}
                      >
                        <span>{topic.icon}</span>
                        {translateTopicName(topic.name)}
                      </button>
                    ))}
                  </div>

                  <div className="onboarding-continue-wrapper">
                    <button onClick={handleTopicsContinue} className="onboarding-continue-btn">
                      {t('common:continue', 'Continue')}
                    </button>
                  </div>
                </div>
              )}

              {/* Theme Selection Step — final step in full onboarding */}
              {isThemeStep && (
                <div className="onboarding-theme-container">
                  <div className="onboarding-theme-list">
                    {ALL_THEMES.map(renderThemeOption)}
                  </div>
                  {isThemeOnlyMode ? (
                    <div className="onboarding-continue-wrapper">
                      <button onClick={() => setOnboardingStep(2)} className="onboarding-continue-btn">
                        {t('common:continue', 'Continue')}
                      </button>
                    </div>
                  ) : (
                    <div className="onboarding-finish-wrapper">
                      <button onClick={handleModalClose} className="onboarding-finish-btn">
                        {t('common:getStarted', 'Get Started')}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default NotificationsOnboarding;